import { FC, SyntheticEvent } from 'react'
import { useIntl } from 'react-intl'
import ResultViewer from '../../utils/ResultViewer/ResultViewer'
import Filters from '../../utils/ResultViewer/Filters'
import Pagination from '../../utils/ResultViewer/Pagination'

type WrapperProps = {
  onEdit: (id: any, refresh: Function) => void
  onDelete: (id: any, refresh: Function) => void
  onAdd?: (event: SyntheticEvent, refresh: Function) => void
}

class EventsResultTable extends ResultViewer {
  render() {
    let limit = this.state.data?.pagination.limit ?? 0
    let page = this.state.page ?? 0
    let totalCount = this.state.data?.pagination.total_count ?? 0
    let viewStart = (page - 1) * limit
    if (viewStart === 0) {
      viewStart = 1
    } else {
      viewStart = viewStart + 1
    }

    let viewEnd = page * limit
    if (viewEnd > totalCount) {
      viewEnd = totalCount
    }
    if (!this.state.data) {
      return <>Loading...</>
    } else {
      return (
        <div className='card mb-5 mb-xl-8'>
          <div className='card-header'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>{this.props.title}</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                Out of {this.state.data?.pagination.total_count ?? 0} records {viewStart} -&nbsp;
                {viewEnd ?? 1} is monitoring.
              </span>
            </h3>
            <div className='align-self-end align-self-center'>
              <Filters
                search={this.state.data.search ?? []}
                setFilterParams={(name: string, value: any) => {
                  this.setFilterParams(name, value)
                }}
                applyFilter={() => {
                  this.fetchData()
                }}
                clearFilters={() => {
                  this.clearFilterParams()
                }}
              />
              {this.state.actionItems?.map((item, index) => {
                return (
                  <button
                    key={index}
                    className={item.class}
                    onClick={(event) => {
                      item.onClick(event, this.fetchData)
                    }}
                  >
                    <span className={item.icon + ' me-2'}></span>
                    {item.text}
                  </button>
                )
              })}
            </div>
          </div>
          <div className='card-body overflow-auto'>
            <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
              <thead>
                <tr>{this.renderHeaders()}</tr>
              </thead>
              <tbody>{this.renderRows()}</tbody>
            </table>
          </div>
          <div className='card-footer'>
            <Pagination {...this.state.data.pagination} setPage={this.setPage} />
          </div>
        </div>
      )
    }
  }

  renderHeaders() {
    if (this.state.data) {
      return Object.keys(this.state.data.headers).map((key) => {
        return this.renderHeader(key)
      })
    } else {
      return <></>
    }
  }

  renderHeader(key: string) {
    if (isNaN(parseInt(key))) {
      let direction =
        this.state.orderBy === key && this.state.orderDirection === 'DESC' ? 'ASC' : 'DESC'
      return (
        <th
          key={key}
          role='button'
          onClick={() => this.setOrderBy(key, direction)}
          className='pe-5'
        >
          <div className='d-flex justify-content-between'>
            {(this.state.data?.headers as any)[key]}
            <span
              className={
                'fa ' +
                (this.state.orderBy === key && direction === 'ASC'
                  ? 'fa-arrow-down'
                  : 'fa-arrow-up')
              }
            ></span>
          </div>
        </th>
      )
    }
    return <th key={key}>{(this.state.data?.headers as any)[key]}</th>
  }

  renderRows() {
    if (this.state.data) {
      return this.state.data.data.map((row: any, index) => {
        return (
          <tr key={index}>
            {Object.keys(row).map((key, colIndex) => {
              return this.renderRow(row, key, colIndex)
            })}
          </tr>
        )
      })
    } else {
      return <></>
    }
  }

  renderRow(row: any, key: string, colIndex: number) {
    if (key === 'edit_actions') {
      return (
        <td key={colIndex}>
          <div className='d-flex justify-content-end flex-shrink-0'>
            <button
              onClick={() => this.props.editCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            >
              <span className='fa fa-edit'></span>
            </button>
            <button
              onClick={() => this.props.deleteCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1'
            >
              <span className='fa fa-trash'></span>
            </button>
          </div>
        </td>
      )
    }
    if (key === 'status') {
      const status = row[key]
      const label =
        status === 'created'
          ? 'Created'
          : status === 'completed'
          ? 'Completed'
          : status === 'cancelled'
          ? 'Cancelled'
          : String(status)
      const badgeClass =
        status === 'created'
          ? 'badge badge-info'
          : status === 'completed'
          ? 'badge badge-success'
          : status === 'cancelled'
          ? 'badge badge-secondary'
          : 'badge badge-light'
      return (
        <td key={colIndex}>
          <span className={badgeClass}>{label}</span>
        </td>
      )
    }
    if (key === 'frequency') {
      const frequency = row[key]
      const label =
        frequency === 'one_time'
          ? 'One Time'
          : frequency === 'daily'
          ? 'Daily'
          : frequency === 'weekly'
          ? 'Weekly'
          : frequency === 'monthly'
          ? 'Monthly'
          : frequency === 'yearly'
          ? 'Yearly'
          : frequency === 'custom'
          ? 'Custom'
          : String(frequency)
      return (
        <td key={colIndex}>
          <span className='badge badge-secondary'>{label}</span>
        </td>
      )
    }
    if (key === 'all_day') {
      const isAllDay = row[key] === 'Yes' || row[key] === '1' || row[key] === 1 || row[key] === true
      return (
        <td key={colIndex} className='text-center'>
          {isAllDay ? (
            <span className='badge badge-light-success'>
              <i className='fa fa-check text-success'></i>
              <span className='ms-1'>Yes</span>
            </span>
          ) : (
            <span className='badge badge-light-danger'>
              <i className='fa fa-times text-danger'></i>
              <span className='ms-1'>No</span>
            </span>
          )}
        </td>
      )
    }
    return (
      <td
        key={colIndex}
        dangerouslySetInnerHTML={{
          __html: row[key],
        }}
      ></td>
    )
  }
}

const EventsTable: FC<WrapperProps> = ({ onEdit, onDelete, onAdd }) => {
  const intl = useIntl()
  return (
    <EventsResultTable
      title={intl.formatMessage({ id: 'MENU.EVENTS' })}
      dataUrl='/events/list'
      editCallback={onEdit}
      deleteCallback={onDelete}
      actionItems={
        onAdd
          ? [
              {
                text: intl.formatMessage({
                  id: 'MENU.EVENTS.ADD_EVENT',
                  defaultMessage: 'Add Event',
                }),
                class: 'btn btn-sm btn-primary',
                icon: 'fa fa-plus',
                onClick: onAdd,
              },
            ]
          : []
      }
    />
  )
}

export default EventsTable
